import React, {useState, useEffect, useRef} from "react";
import RecognitionMessage from "./RecognitionMessage";
import {MapPin, Calendar} from "lucide-react";
import ShoutOutBadge from "./ShoutOutBadge";
import {motion, AnimatePresence} from "framer-motion";
import BubbleBackground from "./../BubbleBackground";
import {Profile} from "../../types/profile";
import {Recognition} from "../../types/recognition";
import { RecognitionType } from "../../types/recognition";
import PaginationDots from "../PaginationDots";


// ===== Constants =====
const SLIDE_INTERVAL = 15000; // Autoplay slide duration in ms
const BUBBLE_COUNT = 12; // Number of bubbles in background
const ANIMATION_DURATION = 1.2; // Base animation duration
const RECOGNITION_DELAY = 0.3; // Staggered delay for recognition message
const MAX_SLIDES = 5;

interface SlideProps {
    isFullscreen?: boolean;
}

const Slide: React.FC<SlideProps> = ({ isFullscreen = false }) => {
    const [current, setCurrent] = useState(0);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const profilesRef = useRef<Profile[]>([]);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    // Fetch profiles and shoutouts
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [profilesRes, shoutoutsRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/employees/slideshow`),
                    fetch(`${API_BASE_URL}/api/shoutout/all`)
                ]);

                const profilesData: Profile[] = await profilesRes.json();
                const shoutoutsData: Recognition[] = await shoutoutsRes.json();

                const latestRecognitions = shoutoutsData
                    .slice()
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .slice(0, MAX_SLIDES);

                const slidesToShow: Profile[] = latestRecognitions.map((rec) => {
                    const receiver = profilesData.find((p) => p.id === rec.receiverId);

                    // fallback if receiver not found
                    return {
                        ...(receiver ?? {
                            id: rec.receiverId,
                            name: rec.receiverName ?? "Unknown",
                            jobTitle: "",
                            location: "",
                            photoUrl: "/assets/slideshowImages/default.jpg"
                        }),
                        recognition: rec
                    };
                });

                setProfiles(slidesToShow);
                profilesRef.current = slidesToShow;
                setCurrent((prev) => {
                    const total = slidesToShow.length;
                    if (total === 0) return 0;
                    return prev % total;
                });

            } catch (err) {
                console.error(err);
            }
        };

        fetchData();

        // Refresh every 10 seconds
        const interval = setInterval(fetchData, 10000);

        return () => clearInterval(interval);
    }, []);

    // Auto-play slides
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrent((prev) => {
                const total = profilesRef.current.length;
                return total === 0 ? 0 : (prev + 1) % total;
            });
        }, SLIDE_INTERVAL);

        return () => clearInterval(interval);
    }, []); // only run once


    if (profiles.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-base md:text-lg text-center px-4">No recognitions yet.
                    Recognitions submitted by your team will appear here.</div>
            </div>
        );
    }

    // Get current slide data
    const profile = profiles[current % profiles.length];
    const recognition = profile.recognition;

    return (
        <div className={`flex flex-col gap-6 px-0 md:px-8 pt-6 pb-0 mx-auto relative w-full ${
            isFullscreen ? "h-full" : "lg:flex-1 lg:min-h-0"
        }`}>
            <BubbleBackground count={BUBBLE_COUNT}/>
            <AnimatePresence mode="wait">
                <motion.div
                    key={current}
                    className="flex flex-col gap-6 w-full lg:flex-1 lg:min-h-0"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: ANIMATION_DURATION, ease: "easeInOut"}}
                >
                    {/* Single Column Layout */}
                    <div className="flex flex-col lg:flex-row gap-8 w-full lg:flex-1 lg:min-h-0">
                        {/* Left Column - Profile Card */}
                        <motion.div
                            className="order-1 lg:order-1 w-full lg:w-[400px] lg:shrink-0 rounded-3xl bg-blue-900/40 backdrop-blur-sm border border-blue-700/50 p-3 md:px-6 md:pt-3 md:pb-6 shadow-2xl flex flex-col relative overflow-auto"
                            initial={{opacity: 0, y: 20}}
                            animate={{opacity: 1, y: 0}}
                            exit={{opacity: 0, y: 20}}
                            transition={{duration: ANIMATION_DURATION, ease: "easeInOut"}}
                        >
                            {/* Recognition Badge - centered */}
                            <div className="flex justify-center items-center mb-4 md:mb-6">
                                <ShoutOutBadge />
                            </div>

                            <div className="relative bg-blue-950 rounded-2xl p-1 mb-4 md:mb-6 shrink-0">
                                <div className="relative rounded-xl overflow-hidden w-full aspect-[4/5]">
                                    <img
                                        src={profile.photoUrl || "/assets/slideshowImages/default.jpg"}
                                        alt={profile.name}
                                        className="absolute inset-0 w-full h-full object-cover object-[center_20%]"
                                    />

                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                                        <h3 className="text-white font-bold text-lg">
                                            {profile.name}
                                        </h3>
                                        <p className="text-gray-300 text-sm">
                                            {profile.jobTitle}
                                        </p>
                                    </div>
                                </div>

                            </div>

                            {/* Profile Details */}
                            <div className="space-y-2">
                                <h3 className="text-lg md:text-3xl font-bold text-white break-words">
                                    {profile.name}
                                </h3>
                                <p className="text-gray-300 text-sm md:text-lg break-words">
                                    {profile.jobTitle}
                                </p>

                                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                                    {/* Location box */}
                                    <div
                                        className="bg-blue-800/40 rounded-xl p-2 flex items-center gap-2 text-gray-300 flex-1">
                                        <MapPin className="w-5 h-5 text-purple-400"/>
                                        <span>{profile.location}</span>
                                    </div>

                                    {/* Date box */}
                                    <div
                                        className="bg-blue-800/40 rounded-xl p-2 flex items-center gap-2 text-gray-300 flex-1">
                                        <Calendar className="w-5 h-5 text-purple-400"/>
                                        <span>
                                             {profile.recognition?.createdAt
                                                 ? new Date(profile.recognition?.createdAt).toLocaleDateString()
                                                 : ""}
                                        </span>
                                    </div>
                                </div>

                                {/* Recognition message Box */}
                                <div className="bg-blue-800/40 rounded-xl p-4 mt-4">
                                    <p className="text-gray-400 text-sm mb-1">Recognition Type</p>
                                    <p className="text-white font-semibold">
                                        {recognition?.type === RecognitionType.PowerUp
                                            ? "Power-up"
                                            : recognition?.type === RecognitionType.ShoutOut
                                                ? "Shout-out"
                                                : "—"}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Right Column - Recognition Message */}
                        <motion.div
                            className="order-2 lg:order-2 w-full lg:flex-1 flex lg:min-h-0"
                            initial={{opacity: 0, y: 20}}
                            animate={{opacity: 1, y: 0}}
                            exit={{opacity: 0, y: 20}}
                            transition={{duration: ANIMATION_DURATION, ease: "easeInOut", delay: RECOGNITION_DELAY}}
                        >
                            <RecognitionMessage
                                message={(recognition?.message ?? "No recognition yet").replace(/@/g, '')}
                                from={recognition?.senderName ?? "—"}
                                to={recognition?.receiverName ?? profile.name}
                            />
                        </motion.div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Pagination Dots */}
            <PaginationDots
                total={profiles.length}
                current={current}
                onSelect={setCurrent}
            />
        </div>
    );
};
export default Slide;
